import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  SyncAlt as SyncAltIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

export type SupplierAliasApi = {
  id: number;
  supplier_id: number;
  pattern_raw: string;
  text_match_operator: string;
  description_contains?: string | null;
  reference_contains?: string | null;
  account_scope: string;
  priority: number;
  is_active: boolean;
  notes?: string | null;
  match_criteria?: Array<{ kind: string; value: string; operator?: string | null }>;
};

type AliasSuggestion = {
  pattern_raw: string;
  recommended_operator: string;
  tx_count: number;
  sources: string[];
};

type AliasFormState = {
  pattern_raw: string;
  description_contains: string;
  reference_contains: string;
  text_match_operator: string;
  account_scope: string;
  priority: number;
  is_active: boolean;
  notes: string;
};

const TEXT_MATCH_OPERATORS = ['exact', 'contains', 'contains_substring', 'not_contains'] as const;
const ACCOUNT_SCOPES = ['both', 'ireland', 'uk'] as const;

function defaultAliasForm(): AliasFormState {
  return {
    pattern_raw: '',
    description_contains: '',
    reference_contains: '',
    text_match_operator: 'contains',
    account_scope: 'both',
    priority: 100,
    is_active: true,
    notes: ''
  };
}

export function truncateMiddle(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) return t;
  const half = Math.floor((max - 3) / 2);
  return `${t.slice(0, half)}…${t.slice(-half)}`;
}

export function formatSupplierAliasLabel(a: SupplierAliasApi): string {
  const parts: string[] = [];
  if ((a.pattern_raw || '').trim()) parts.push((a.pattern_raw || '').trim());
  const dc = (a.description_contains || '').trim();
  if (dc) parts.push(`desc: ${dc}`);
  const rc = (a.reference_contains || '').trim();
  if (rc) parts.push(`ref: ${rc}`);
  if (!parts.length && Array.isArray(a.match_criteria) && a.match_criteria.length) {
    return a.match_criteria
      .slice(0, 3)
      .map((m) => `${m.kind}:${m.value}`)
      .join(' · ');
  }
  return parts.join(' · ') || `Rule #${a.id}`;
}

export type SupplierAliasMappingsDialogProps = {
  open: boolean;
  supplierId: string | number | null;
  supplierName: string;
  onClose: () => void;
  canManageSupplierAliases: boolean;
  /** Refresh parent chips / supplier-rule datasets after mutations */
  onAliasesChanged?: () => void | Promise<void>;
};

export const SupplierAliasMappingsDialog: React.FC<SupplierAliasMappingsDialogProps> = ({
  open,
  supplierId,
  supplierName,
  onClose,
  canManageSupplierAliases,
  onAliasesChanged
}) => {
  const [loading, setLoading] = useState(false);
  const [rowsForSupplier, setRowsForSupplier] = useState<SupplierAliasApi[]>([]);
  const [suggestions, setSuggestions] = useState<AliasSuggestion[]>([]);
  const [modalError, setModalError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingAliasId, setEditingAliasId] = useState<number | null>(null);
  const [aliasForm, setAliasForm] = useState<AliasFormState>(() => defaultAliasForm());
  const [applyBusyId, setApplyBusyId] = useState<number | null>(null);

  const sidStr = supplierId != null ? String(supplierId) : '';

  const reload = useCallback(async () => {
    if (!sidStr) return;
    setLoading(true);
    setModalError('');
    try {
      const [aliasesRes, sugRes] = await Promise.all([
        api.get('/api/suppliers/description-aliases'),
        api.get(`/api/suppliers/${sidStr}/description-alias-suggestions`)
      ]);
      const list = (aliasesRes.data?.aliases || []) as SupplierAliasApi[];
      const mine = list.filter((r) => String(r.supplier_id) === sidStr);
      setRowsForSupplier(mine);
      setSuggestions(Array.isArray(sugRes.data?.suggestions) ? sugRes.data.suggestions : []);
    } catch (err: any) {
      setModalError(err.response?.data?.error || 'Failed to load mappings.');
    } finally {
      setLoading(false);
    }
  }, [sidStr]);

  useEffect(() => {
    if (!open || !sidStr) {
      setRowsForSupplier([]);
      setSuggestions([]);
      setModalError('');
      setEditingAliasId(null);
      setAliasForm(defaultAliasForm());
      setApplyBusyId(null);
      return;
    }
    void reload();
  }, [open, sidStr, reload]);

  const notifyParent = useCallback(async () => {
    await onAliasesChanged?.();
  }, [onAliasesChanged]);

  const refreshSuggestionsOnly = async () => {
    if (!sidStr) return;
    try {
      const sugRes = await api.get(`/api/suppliers/${sidStr}/description-alias-suggestions`);
      setSuggestions(Array.isArray(sugRes.data?.suggestions) ? sugRes.data.suggestions : []);
    } catch {
      /* ignore */
    }
  };

  const buildPayload = () => {
    const pattern_raw = aliasForm.pattern_raw.trim();
    const description_contains = aliasForm.description_contains.trim();
    const reference_contains = aliasForm.reference_contains.trim();
    return {
      supplier_id: Number(supplierId),
      pattern_raw: pattern_raw || undefined,
      description_contains: description_contains || undefined,
      reference_contains: reference_contains || undefined,
      text_match_operator: aliasForm.text_match_operator,
      account_scope: aliasForm.account_scope,
      priority: Number(aliasForm.priority) || 100,
      is_active: aliasForm.is_active,
      notes: aliasForm.notes.trim() || undefined
    };
  };

  const handleSave = async () => {
    if (!sidStr || !canManageSupplierAliases) return;
    const payload = buildPayload();
    if (!payload.pattern_raw && !payload.description_contains && !payload.reference_contains) {
      setModalError('Add at least one of merchant/counterparty text, description contains, or reference contains.');
      return;
    }
    setBusy(true);
    setModalError('');
    try {
      if (editingAliasId != null) {
        await api.put(`/api/suppliers/description-aliases/${editingAliasId}`, payload);
      } else {
        await api.post('/api/suppliers/description-aliases', payload);
      }
      await reload();
      await notifyParent();
      await refreshSuggestionsOnly();
      setEditingAliasId(null);
      setAliasForm(defaultAliasForm());
    } catch (err: any) {
      setModalError(err.response?.data?.error || 'Failed to save mapping.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (aliasId: number) => {
    if (!canManageSupplierAliases) return;
    if (!window.confirm('Delete this supplier mapping rule?')) return;
    setBusy(true);
    setModalError('');
    try {
      await api.delete(`/api/suppliers/description-aliases/${aliasId}`);
      await reload();
      await notifyParent();
      await refreshSuggestionsOnly();
      if (editingAliasId === aliasId) {
        setEditingAliasId(null);
        setAliasForm(defaultAliasForm());
      }
    } catch (err: any) {
      setModalError(err.response?.data?.error || 'Failed to delete mapping.');
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async (aliasId: number) => {
    if (!canManageSupplierAliases) return;
    setApplyBusyId(aliasId);
    setModalError('');
    try {
      await api.post(`/api/suppliers/description-aliases/${aliasId}/apply-single`, {});
      await reload();
      await notifyParent();
      await refreshSuggestionsOnly();
    } catch (err: any) {
      setModalError(err.response?.data?.error || 'Failed to apply rule to tagged transactions.');
    } finally {
      setApplyBusyId(null);
    }
  };

  const startEdit = (a: SupplierAliasApi) => {
    setEditingAliasId(a.id);
    setAliasForm({
      pattern_raw: (a.pattern_raw || '').trim(),
      description_contains: (a.description_contains || '').trim(),
      reference_contains: (a.reference_contains || '').trim(),
      text_match_operator: a.text_match_operator || 'contains',
      account_scope: a.account_scope || 'both',
      priority: typeof a.priority === 'number' ? a.priority : 100,
      is_active: a.is_active !== false,
      notes: (a.notes || '').trim()
    });
    setModalError('');
  };

  const handleAddSuggested = async (s: AliasSuggestion) => {
    if (!sidStr || !canManageSupplierAliases) return;
    setBusy(true);
    setModalError('');
    try {
      await api.post('/api/suppliers/description-aliases', {
        supplier_id: Number(supplierId),
        pattern_raw: s.pattern_raw,
        text_match_operator: s.recommended_operator,
        account_scope: 'both',
        priority: 150,
        is_active: true
      });
      await reload();
      await notifyParent();
      await refreshSuggestionsOnly();
    } catch (err: any) {
      setModalError(err.response?.data?.error || 'Failed to add suggested mapping.');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Statement mappings
        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Canonical supplier: <strong>{supplierName}</strong>
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 'min(85vh, 720px)' }}>
        {!canManageSupplierAliases && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only owner or admin accounts can create or change supplier mapping rules. You can still review what is
            configured.
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Stack spacing={3}>
            {modalError && (
              <Alert severity="error" onClose={() => setModalError('')}>
                {modalError}
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Active rules
              </Typography>
              {rowsForSupplier.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No mapping rules yet. Add one below or accept a suggestion.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={buildFitColgroup([
                        { key: 'match' },
                        { key: 'operator', pct: 13 },
                        { key: 'scope', pct: 11 },
                        { key: 'priority', pct: 7 },
                        { key: 'active', pct: 9 },
                        { key: 'actions', pct: 18 },
                      ])}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell>Match</TableCell>
                        <TableCell>Operator</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Pri</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rowsForSupplier.map((a) => {
                        const legacyEditable = !!(
                          a.pattern_raw?.trim() ||
                          a.description_contains?.trim() ||
                          a.reference_contains?.trim()
                        );
                        return (
                          <TableRow key={a.id}>
                            <TableCell>
                              <Box sx={fitTruncateSx} title={formatSupplierAliasLabel(a)}>
                                {formatSupplierAliasLabel(a)}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={fitTruncateSx} title={a.text_match_operator}>
                                {a.text_match_operator}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={fitTruncateSx} title={a.account_scope}>
                                {a.account_scope}
                              </Box>
                            </TableCell>
                            <TableCell>{a.priority}</TableCell>
                            <TableCell>{a.is_active ? 'Yes' : 'No'}</TableCell>
                            <TableCell align="right" className="fit-table-no-clip">
                              <Tooltip
                                title={
                                  legacyEditable
                                    ? 'Edit matchers'
                                    : 'Advanced criteria — edit under Supplier rules table on this page'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canManageSupplierAliases || !legacyEditable || busy}
                                    onClick={() => startEdit(a)}
                                    aria-label="Edit mapping"
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Apply canonical supplier name to matching tagged transactions">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canManageSupplierAliases || busy || applyBusyId === a.id}
                                    onClick={() => handleApply(a.id)}
                                    aria-label="Apply mapping"
                                  >
                                    {applyBusyId === a.id ? <CircularProgress size={18} /> : <SyncAltIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Delete rule">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!canManageSupplierAliases || busy}
                                    onClick={() => handleDelete(a.id)}
                                    aria-label="Delete mapping"
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {editingAliasId != null ? `Edit rule #${editingAliasId}` : 'Add mapping'}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Merchant / counterparty text"
                  size="small"
                  fullWidth
                  value={aliasForm.pattern_raw}
                  onChange={(e) => setAliasForm((p) => ({ ...p, pattern_raw: e.target.value }))}
                  helperText="Matches Revolut merchant or counterparty name"
                />
                <TextField
                  label="Description contains (optional)"
                  size="small"
                  fullWidth
                  value={aliasForm.description_contains}
                  onChange={(e) => setAliasForm((p) => ({ ...p, description_contains: e.target.value }))}
                />
                <TextField
                  label="Reference contains (optional)"
                  size="small"
                  fullWidth
                  value={aliasForm.reference_contains}
                  onChange={(e) => setAliasForm((p) => ({ ...p, reference_contains: e.target.value }))}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Merchant operator</InputLabel>
                    <Select
                      label="Merchant operator"
                      value={aliasForm.text_match_operator}
                      onChange={(e) => setAliasForm((p) => ({ ...p, text_match_operator: String(e.target.value) }))}
                    >
                      {TEXT_MATCH_OPERATORS.map((op) => (
                        <MenuItem key={op} value={op}>
                          {op}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Account scope</InputLabel>
                    <Select
                      label="Account scope"
                      value={aliasForm.account_scope}
                      onChange={(e) => setAliasForm((p) => ({ ...p, account_scope: String(e.target.value) }))}
                    >
                      {ACCOUNT_SCOPES.map((sc) => (
                        <MenuItem key={sc} value={sc}>
                          {sc}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Priority"
                    type="number"
                    size="small"
                    sx={{ width: 120 }}
                    value={aliasForm.priority}
                    onChange={(e) => setAliasForm((p) => ({ ...p, priority: Number(e.target.value) || 0 }))}
                    helperText="Lower runs first"
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={aliasForm.is_active}
                    onChange={(e) => setAliasForm((p) => ({ ...p, is_active: e.target.checked }))}
                    size="small"
                  />
                  <Typography variant="body2">Rule active</Typography>
                </Stack>
                <TextField
                  label="Notes (optional)"
                  size="small"
                  fullWidth
                  value={aliasForm.notes}
                  onChange={(e) => setAliasForm((p) => ({ ...p, notes: e.target.value }))}
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" disabled={!canManageSupplierAliases || busy} onClick={handleSave}>
                    {busy ? <CircularProgress size={22} /> : editingAliasId != null ? 'Save changes' : 'Add rule'}
                  </Button>
                  {editingAliasId != null && (
                    <Button
                      disabled={busy}
                      onClick={() => {
                        setEditingAliasId(null);
                        setAliasForm(defaultAliasForm());
                        setModalError('');
                      }}
                    >
                      Cancel edit
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Suggested from your transactions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Based on transactions already tagged with this supplier (merchant/counterparty/description text).
                Review before adding — broad “contains” rules can match unintended rows.
              </Typography>
              {suggestions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No additional suggestions (either nothing tagged yet, or suggestions already have rules).
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={FIT_TABLE_SX}>
                    <FitColgroup
                      cols={buildFitColgroup([
                        { key: 'sample' },
                        { key: 'operator', pct: 16 },
                        { key: 'count', pct: 11 },
                        { key: 'add', pct: 16 },
                      ])}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell>Sample statement text</TableCell>
                        <TableCell>Suggested op</TableCell>
                        <TableCell>Tx count</TableCell>
                        <TableCell align="right">
                          Add
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {suggestions.map((s, idx) => (
                        <TableRow key={`${idx}:${s.pattern_raw}:${s.recommended_operator}`}>
                          <TableCell>
                            <Box sx={fitTruncateSx} title={s.pattern_raw}>
                              {s.pattern_raw}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={fitTruncateSx} title={s.recommended_operator}>
                              {s.recommended_operator}
                            </Box>
                          </TableCell>
                          <TableCell>{s.tx_count}</TableCell>
                          <TableCell align="right" className="fit-table-no-clip">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!canManageSupplierAliases || busy}
                              onClick={() => handleAddSuggested(s)}
                            >
                              Add rule
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
